import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    Paper,
    PaperProps,
    Tooltip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Close as CloseIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';

export interface RootDialogPageProps {
    open: boolean;
    onClose: () => void;
    title: string;
    icon?: React.ReactNode;
    onAction?: () => void;
    actionLabel?: string;
    actionDisabled?: boolean;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    fullWidth?: boolean;
    defaultWidth?: number;
    defaultHeight?: number;
    resizable?: boolean;
    children: React.ReactNode;
}

function PaperComponent(props: PaperProps) {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"], [class*="MuiIconButton-root"], [class*="MuiButton-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

const RootDialogPage: React.FC<RootDialogPageProps> = ({
    open,
    onClose,
    title,
    icon,
    onAction,
    actionLabel,
    actionDisabled = false,
    maxWidth,
    fullWidth = false,
    defaultWidth = 600,
    defaultHeight = 400,
    resizable = false,
    children
}) => {
    const { t } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [size, setSize] = useState({ width: defaultWidth, height: defaultHeight });
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        if (!open) setIsFullscreen(false);
    }, [open]);

    const useFullScreen = isMobile || isFullscreen;

    const onResize = (_event: any, { size: nextSize }: any) => {
        setSize({ width: nextSize.width, height: nextSize.height });
    };

    const titleBar = (
        <DialogTitle
            style={{ cursor: useFullScreen ? 'default' : 'move' }}
            id="draggable-dialog-title"
            sx={{
                p: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
            }}
        >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                    {icon && (
                        <Box sx={{ color: 'inherit', display: 'flex', opacity: 0.9, flexShrink: 0 }}>
                            {icon}
                        </Box>
                    )}
                    <Typography variant="h6" component="div" sx={{ fontWeight: 600 }} noWrap>
                        {title}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                    {!isMobile && (
                        <Tooltip title={isFullscreen ? t('common.actions.exitFullScreen') : t('common.actions.enterFullScreen')}>
                            <IconButton
                                aria-label="fullscreen"
                                onClick={() => setIsFullscreen(prev => !prev)}
                                sx={{
                                    color: 'inherit',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                                }}
                                size="small"
                            >
                                {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: 'inherit',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        </DialogTitle>
    );

    const footer = (
        <DialogActions sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider'
        }}>
            <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
                {t('btn.cancel')}
            </Button>
            {onAction && (
                <Button
                    onClick={onAction}
                    variant="contained"
                    color="primary"
                    disabled={actionDisabled}
                    sx={{
                        px: 3,
                        borderRadius: 50,
                        textTransform: 'none',
                        fontWeight: 'bold'
                    }}
                >
                    {actionLabel || t('common.actions.save')}
                </Button>
            )}
        </DialogActions>
    );

    const dialogContent = (
        <Box sx={{
            width: (resizable && !useFullScreen) ? size.width : '100%',
            height: useFullScreen
                ? '100%'
                : ((resizable && !isMobile) ? size.height : 'auto'),
            display: 'flex',
            flexDirection: 'column',
            maxHeight: useFullScreen ? '100%' : ((resizable || isMobile) ? 'unset' : 'calc(100vh - 64px)'),
        }}>
            {titleBar}
            <DialogContent dividers sx={{
                p: 3,
                flexGrow: 1,
                bgcolor: 'background.paper',
                overflowY: 'auto'
            }}>
                {children}
            </DialogContent>
            {footer}
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={resizable && !useFullScreen ? false : maxWidth}
            fullWidth={fullWidth}
            fullScreen={useFullScreen}
            PaperComponent={useFullScreen ? Paper : PaperComponent}
            PaperProps={{
                sx: {
                    borderRadius: useFullScreen ? 0 : 3,
                    overflow: 'hidden',
                    m: 0,
                    maxWidth: (resizable && !useFullScreen) ? 'none' : undefined,
                    height: useFullScreen ? '100%' : 'auto'
                }
            }}
            aria-labelledby="draggable-dialog-title"
        >
            {useFullScreen ? (
                dialogContent
            ) : (resizable ? (
                <Resizable
                    width={size.width}
                    height={size.height}
                    onResize={onResize}
                    minConstraints={[400, 300]}
                    maxConstraints={[1200, 900]}
                    handle={<Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        width: 20,
                        height: 20,
                        cursor: 'se-resize',
                        zIndex: 10,
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 4,
                            bottom: 4,
                            width: 8,
                            height: 8,
                            borderRight: `2px solid ${theme.palette.text.secondary}`,
                            borderBottom: `2px solid ${theme.palette.text.secondary}`
                        }
                    }} />}
                >
                    {dialogContent}
                </Resizable>
            ) : (
                dialogContent
            ))}
        </Dialog>
    );
};

export default RootDialogPage;
